import { useEffect, useRef, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Button, Input, FormField } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import {
  brokeragesApi,
  type BrokerageSearchResultDto,
  type UpsertBrokeragePayload,
} from "@/api/brokerages";

export interface BrokeragePickerResult {
  brokerageId: string;
  brokerageName: string;
}

interface BrokeragePickerProps {
  onSelect: (result: BrokeragePickerResult) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Disables the picker (e.g. while a parent mutation is in flight). */
  disabled?: boolean;
}

const DEBOUNCE_MS = 250;

/**
 * Brokerage Module v1.0 — autocomplete search over <c>GET /api/brokerages/search</c>
 * with an inline "+ Create new brokerage" affordance (Name required, rest
 * optional) that creates the firm without leaving the page and auto-selects it.
 */
export function BrokeragePicker({ onSelect, placeholder, autoFocus, disabled }: BrokeragePickerProps) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState<BrokerageSearchResultDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = useRef("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  function scheduleSearch(query: string) {
    lastQueryRef.current = query;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await brokeragesApi.search(query, 10);
        if (lastQueryRef.current === query) setMatches(results);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }

  function commit(result: BrokeragePickerResult) {
    setText(result.brokerageName);
    setOpen(false);
    onSelect(result);
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search
          size={14}
          strokeWidth={1.5}
          aria-hidden
          className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid pointer-events-none"
        />
        <Input
          type="text"
          value={text}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder ?? "Search brokerages…"}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-label="Brokerage"
          className="pl-7"
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            // A search typeahead must never submit its enclosing <form>.
            // Containment only — selection here stays click-driven; full
            // keyboard navigation (arrow highlight + Enter-to-select) is
            // deliberately out of scope.
            if (e.key === "Enter") e.preventDefault();
          }}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            scheduleSearch(e.target.value);
          }}
        />
      </div>

      {open && (
        <ul
          role="listbox"
          className="absolute z-30 mt-space-1 w-full max-h-64 overflow-auto bg-white-pure border border-border-light rounded-md shadow-lg py-space-1"
        >
          {loading && (
            <li className="px-space-3 py-space-2 text-caption text-gray-mid">Searching…</li>
          )}
          {!loading && matches.length === 0 && (
            <li className="px-space-3 py-space-2 text-caption text-gray-mid">No matches.</li>
          )}
          {matches.map((m) => (
            <li key={m.id} role="option" aria-selected={false}>
              <button
                type="button"
                className="w-full text-left px-space-3 py-space-2 text-small hover:bg-white-warm"
                onClick={() => commit({ brokerageId: m.id, brokerageName: m.name })}
              >
                <span className="font-semibold">{m.name}</span>
                {m.dba && <span className="text-caption text-gray-mid"> — DBA {m.dba}</span>}
                {(m.city || m.state) && (
                  <span className="text-caption text-gray-mid"> · {[m.city, m.state].filter(Boolean).join(", ")}</span>
                )}
              </button>
            </li>
          ))}
          <li className="border-t border-border-light mt-space-1 pt-space-1">
            <button
              type="button"
              className="w-full text-left px-space-3 py-space-2 text-small text-gold-shadow hover:bg-white-warm flex items-center gap-space-1"
              onClick={() => { setOpen(false); setCreateOpen(true); }}
            >
              <Plus size={14} strokeWidth={1.5} aria-hidden /> Create new brokerage
            </button>
          </li>
        </ul>
      )}

      {createOpen && (
        <CreateBrokerageModal
          initialName={text}
          onClose={() => setCreateOpen(false)}
          onCreated={(result) => { setCreateOpen(false); commit(result); }}
        />
      )}
    </div>
  );
}

function CreateBrokerageModal({
  initialName,
  onClose,
  onCreated,
}: {
  initialName: string;
  onClose: () => void;
  onCreated: (result: BrokeragePickerResult) => void;
}) {
  const [form, setForm] = useState<UpsertBrokeragePayload>({
    name: initialName ?? "",
    phone: "",
    website: "",
    city: "",
    state: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof UpsertBrokeragePayload>(k: K, v: UpsertBrokeragePayload[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setError(null);
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await brokeragesApi.create({ ...form, name: form.name.trim() });
      onCreated({ brokerageId: created.id, brokerageName: created.name });
    } catch (e) {
      setError((e as Error).message ?? "Create failed.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="New brokerage"
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" loading={saving} disabled={!form.name.trim()} onClick={save}>
            Create
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3">
        <FormField label="Name" required>
          <Input value={form.name} autoFocus onChange={(e) => set("name", e.target.value)} />
        </FormField>
        <div className="grid grid-cols-2 gap-space-3">
          <FormField label="Phone">
            <Input value={form.phone ?? ""} onChange={(e) => set("phone", e.target.value)} />
          </FormField>
          <FormField label="Website">
            <Input value={form.website ?? ""} onChange={(e) => set("website", e.target.value)} />
          </FormField>
          <FormField label="City">
            <Input value={form.city ?? ""} onChange={(e) => set("city", e.target.value)} />
          </FormField>
          <FormField label="State">
            <Input value={form.state ?? ""} maxLength={2} onChange={(e) => set("state", e.target.value)} />
          </FormField>
        </div>
        {error && <p className="text-label text-status-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}
